import {
  getHeaderImage,
  CHATEAU_LOGO,
  getContrastingBrandliveLogo,
  FOOTER_CARD_STYLE,
  getSessionDateLabel,
  getSessionTimeLabel,
  resolveThemedColor,
  resolveThemedFont,
  resolveThemedRadius,
  formatDateParts,
} from './utils/utils';
import { ECustomDesignTypes, EPaletteModes } from '@/types/theme-packs.types';
import type { TemplateProps } from './campaign-template.types';
import ContentEditable from './content-editable';
import styles from './template-styles.module.css';
import HeaderWidget from './widgets/header-widget';
import ImageWidget from './widgets/image-widget';
import ParagraphWidget from './widgets/paragraph-widget';
import FooterWidget from './widgets/footer-widget';
import LogoWidget from './widgets/logo-widget';
import SocialWidget from './widgets/social-widget';
import { ICONS } from '@ui/icons/types';
import { templateStaticDefaults } from './template-defaults';
import { BlockRenderer } from './widgets/block-renderer';
import StyledBlockBox from './widgets/styled-block-box';
import BlockShell from '../editor/components/block-shell';
import CalendarLinksWidget from './widgets/calendar-links-widget';
import { BLOCK_ID } from './widgets/block-ids';

const DEFAULTS = templateStaticDefaults.chateau;

const CHATEAU_BLOCK_PRESETS: Record<string, Record<string, unknown>> = {
  [BLOCK_ID.topBar]: { background: 'var(--accent)' },
  [BLOCK_ID.headerLogo]: { height: 28, padding: '32px 56px' },
  [BLOCK_ID.title]: {
    color: 'var(--heading)',
    fontFamily: 'var(--heading-font)',
    fontSize: '40px',
    fontWeight: 400,
    lineHeight: '48px',
    textAlign: 'center',
    padding: '20px 56px',
  },
  [BLOCK_ID.mainImage]: {
    size: 'original',
    width: 400,
    height: 200,
    borderRadius: 'var(--image-radius)',
    padding: '20px 56px',
  },
  [BLOCK_ID.cta]: {
    height: '48px',
    fontSize: '14px',
    buttonPadding: '0 40px',
    color: 'var(--button-text)',
    buttonColor: 'var(--button-color)',
    fontFamily: 'var(--body-font)',
    fontWeight: 600,
  },
  [BLOCK_ID.social]: { color: 'var(--heading)', padding: '20px 56px 0 56px' },
  [BLOCK_ID.tagline]: {
    color: 'var(--body)',
    fontSize: '12px',
    fontFamily: 'var(--body-font)',
    textAlign: 'center',
    opacity: 0.75,
    width: 300,
    padding: '20px 0 40px 0',
  },
  [BLOCK_ID.mailingAddress]: { ...FOOTER_CARD_STYLE, fontFamily: 'var(--body-font)' },
};

const TOP_BAR_BLOCK_ID: string = BLOCK_ID.topBar;
const COUNTDOWN_BADGE_BLOCK_ID: string = BLOCK_ID.countdownBadge;
const INFO_CARD_BLOCK_ID: string = BLOCK_ID.infoCard;

export const Chateau = ({
  event,
  theme,
  mode,
  timezone,
  href,
  calendarLinks,
  customizations,
  title,
  setTitle,
  cta,
  setCta,
  setMainImage,
  setHeaderImage,
  blocks,
  onUpdateBlockValue,
  onUpdateBlockImage,
  onDeleteBlock,
  onMoveBlock,
  selectedBlockId,
  onSelectBlock,
  isEditMode,
  hideBrandliveLogo,
  privacyPolicyLabel,
}: TemplateProps) => {
  const resolvedMode = mode ?? theme?.theme_mode ?? EPaletteModes.Light;
  const themeColors = theme?.colors?.[resolvedMode] ?? theme?.colors;
  const bg = resolveThemedColor(
    customizations?.backgroundColor,
    themeColors?.backgroundColor,
    DEFAULTS.backgroundColor
  );
  const container = resolveThemedColor(
    customizations?.containerColor,
    themeColors?.containerColor,
    DEFAULTS.containerColor
  );
  const accent = resolveThemedColor(customizations?.accentColor, themeColors?.accentColor, DEFAULTS.accentColor);
  const heading = resolveThemedColor(
    customizations?.headingTextColor,
    themeColors?.headingTextColor,
    DEFAULTS.headingTextColor
  );
  const body = resolveThemedColor(customizations?.bodyTextColor, themeColors?.bodyTextColor, DEFAULTS.bodyTextColor);
  const buttonText = resolveThemedColor(
    customizations?.buttonTextColor,
    themeColors?.buttonTextColor,
    DEFAULTS.buttonTextColor ?? accent
  );
  const buttonColor = DEFAULTS.buttonColor ?? accent;
  const buttonRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.button], '100px');
  const containerRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.container], '0px');
  const imageRadius = resolveThemedRadius(theme?.custom_styles?.[ECustomDesignTypes.img], '4px');
  const sessionTimestamp = event.sessions[0]?.timestamp;
  const sessionEndTimestamp = event.sessions[0]?.end_timestamp;
  const daysUntil = sessionTimestamp
    ? Math.max(0, Math.ceil((sessionTimestamp - Date.now()) / (24 * 60 * 60 * 1000)))
    : null;
  const sessionDateLabel = getSessionDateLabel(sessionTimestamp, timezone, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const sessionTimeLabel = getSessionTimeLabel(sessionTimestamp, timezone);
  const sessionDateParts = sessionTimestamp ? formatDateParts(sessionTimestamp, timezone) : null;
  const durationMinutes =
    sessionTimestamp && sessionEndTimestamp ? Math.round((sessionEndTimestamp - sessionTimestamp) / 60000) : null;
  const anchorHref = href || '#';
  const headerImg = customizations?.headingImage || getHeaderImage(event) || CHATEAU_LOGO;
  const bodyImg = customizations?.mainImage || DEFAULTS.image;
  const headingFont = resolveThemedFont(
    customizations?.headingFont,
    theme?.font_pack?.heading_name,
    '"Plus Jakarta Sans", Arial, serif'
  );
  const bodyFont = resolveThemedFont(
    customizations?.bodyFont,
    theme?.font_pack?.body_name,
    '"Plus Jakarta Sans", Arial, sans-serif'
  );
  const eventTitle =
    title ?? (customizations?.title || `Your Show Is in ${daysUntil ?? ''} Day${daysUntil === 1 ? '' : 's'}`);
  const _cta = cta ?? (customizations?.cta || DEFAULTS.cta);

  const bodyColor = container;
  const footerTextColor = body;
  const footerBackground = bg;
  const brandliveLogo = hideBrandliveLogo ? undefined : getContrastingBrandliveLogo(bodyColor);

  if (blocks?.length) {
    const theme = {
      bg,
      container,
      accent,
      buttonColor,
      heading,
      buttonText,
      body,
      headingFont,
      bodyFont,
      imageRadius,
      buttonRadius,
      containerRadius,
      bodyBg: bodyColor,
      footerText: footerTextColor,
      footerBg: footerBackground,
    };
    const blockContext = {
      blockPresets: CHATEAU_BLOCK_PRESETS,
      brandliveLogo,
      privacyPolicyLabel,
      href: anchorHref,
      theme,
      dateLabel: timezone ? sessionDateLabel : null,
      timeLabel: timezone ? sessionTimeLabel : null,
      dateParts: timezone ? sessionDateParts : null,
    };

    return (
      <table
        data-component="Chateau"
        width="100%"
        cellPadding={0}
        cellSpacing={0}
        border={0}
        role="presentation"
        className={styles.email}
        style={
          {
            '--bg': bg,
            '--container': container,
            '--accent': accent,
            '--button-color': buttonColor,
            '--heading': heading,
            '--body': body,
            '--button-text': buttonText,
            '--heading-font': headingFont,
            '--body-font': bodyFont,
            '--image-radius': imageRadius,
            '--button-radius': buttonRadius,
            background: 'var(--bg)',
          } as React.CSSProperties
        }
      >
        <tbody>
          <tr>
            <td align="center" style={{ padding: '0 32px' }}>
              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px', background: 'var(--container)' }}
              >
                <tbody>
                  <tr>
                    <td>
                      {blocks.map((block) => {
                        if (block.id === TOP_BAR_BLOCK_ID) {
                          return (
                            <BlockShell
                              key={block.id}
                              block={block}
                              isEditMode={!!isEditMode}
                              isFirst={block.id === blocks[0]?.id}
                              isLast={block.id === blocks[blocks.length - 1]?.id}
                              selected={block.id === selectedBlockId}
                              onSelect={onSelectBlock}
                              onDelete={onDeleteBlock}
                              onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                              onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                            >
                              <table
                                role="presentation"
                                width="100%"
                                cellPadding={0}
                                cellSpacing={0}
                                border={0}
                                style={{ background: 'var(--accent)' }}
                              >
                                <tbody>
                                  <tr>
                                    <td height={4} style={{ fontSize: 0, lineHeight: '0' }}>
                                      &nbsp;
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </BlockShell>
                          );
                        }

                        if (block.id === COUNTDOWN_BADGE_BLOCK_ID) {
                          if (daysUntil === null) return null;
                          return (
                            <BlockShell
                              key={block.id}
                              block={block}
                              isEditMode={!!isEditMode}
                              isFirst={block.id === blocks[0]?.id}
                              isLast={block.id === blocks[blocks.length - 1]?.id}
                              selected={block.id === selectedBlockId}
                              onSelect={onSelectBlock}
                              onDelete={onDeleteBlock}
                              onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                              onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                            >
                              <StyledBlockBox
                                blockId={block.id}
                                style={block.style}
                                defaults={{ margin: '20px 56px', background: 'var(--bg)', borderRadius: '60px' }}
                                align="center"
                                contentAlign="center"
                                fitContent
                                width={120}
                                height={120}
                              >
                                <div
                                  style={{
                                    color: 'var(--accent)',
                                    fontSize: '64px',
                                    fontFamily: 'var(--body-font)',
                                  }}
                                >
                                  {daysUntil}
                                </div>
                              </StyledBlockBox>
                            </BlockShell>
                          );
                        }

                        if (block.id === INFO_CARD_BLOCK_ID) {
                          if (!timezone) return null;
                          if (!sessionDateLabel && !sessionTimeLabel && durationMinutes === null) return null;
                          const rowStyle = {
                            color: 'var(--body)',
                            fontSize: '14px',
                            fontFamily: 'var(--body-font)',
                            textAlign: 'center' as const,
                          };
                          return (
                            <BlockShell
                              key={block.id}
                              block={block}
                              isEditMode={!!isEditMode}
                              isFirst={block.id === blocks[0]?.id}
                              isLast={block.id === blocks[blocks.length - 1]?.id}
                              selected={block.id === selectedBlockId}
                              onSelect={onSelectBlock}
                              onDelete={onDeleteBlock}
                              onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                              onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                            >
                              <StyledBlockBox
                                blockId={block.id}
                                style={block.style}
                                defaults={{ margin: '0 56px' }}
                                align="center"
                              >
                                {sessionDateLabel && (
                                  <div style={{ ...rowStyle, padding: '12px 0' }}>{sessionDateLabel}</div>
                                )}
                                {sessionTimeLabel && (
                                  <div style={{ ...rowStyle, padding: '12px 0' }}>{sessionTimeLabel}</div>
                                )}
                                {durationMinutes !== null && (
                                  <div style={{ ...rowStyle, padding: '12px 0' }}>
                                    {durationMinutes} Minutes Duration
                                  </div>
                                )}
                              </StyledBlockBox>
                            </BlockShell>
                          );
                        }

                        // the title has no static default text - it's always the live
                        // "N days until" countdown (see `eventTitle` above) until a user
                        // actually types their own override into the block
                        const renderBlock =
                          block.id === BLOCK_ID.title && !block.props.value
                            ? { ...block, props: { ...block.props, value: eventTitle } }
                            : block;

                        return (
                          <BlockShell
                            key={block.id}
                            block={block}
                            isEditMode={!!isEditMode}
                            isFirst={block.id === blocks[0]?.id}
                            isLast={block.id === blocks[blocks.length - 1]?.id}
                            selected={block.id === selectedBlockId}
                            onSelect={onSelectBlock}
                            onDelete={onDeleteBlock}
                            onMoveUp={(id) => onMoveBlock?.(id, 'up')}
                            onMoveDown={(id) => onMoveBlock?.(id, 'down')}
                          >
                            <BlockRenderer
                              block={renderBlock}
                              stylePreset={CHATEAU_BLOCK_PRESETS[block.id]}
                              onValueChange={onUpdateBlockValue}
                              onImageChange={onUpdateBlockImage}
                              context={blockContext}
                            />
                          </BlockShell>
                        );
                      })}
                    </td>
                  </tr>
                </tbody>
              </table>

              <table
                width={600}
                cellPadding={0}
                cellSpacing={0}
                border={0}
                role="presentation"
                style={{ maxWidth: '600px' }}
              >
                <tbody>
                  {!!calendarLinks && (
                    <tr>
                      <td align="center" style={{ padding: '24px 0' }}>
                        <CalendarLinksWidget links={calendarLinks} color="var(--body)" padding="0" />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </td>
          </tr>
        </tbody>
      </table>
    );
  }

  return (
    <table
      data-component="Chateau"
      width="100%"
      cellPadding={0}
      cellSpacing={0}
      border={0}
      role="presentation"
      className={styles.email}
      style={
        {
          '--bg': bg,
          '--container': container,
          '--accent': accent,
          '--button-color': buttonColor,
          '--heading': heading,
          '--body': body,
          '--button-text': buttonText,
          '--heading-font': headingFont,
          '--body-font': bodyFont,
          background: 'var(--bg)',
        } as React.CSSProperties
      }
    >
      <tbody>
        <tr>
          <td align="center" style={{ padding: '0 32px' }}>
            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px', background: 'var(--container)' }}
            >
              <tbody>
                <tr>
                  <td height={4} style={{ background: 'var(--accent)', fontSize: 0, lineHeight: '0' }}>
                    &nbsp;
                  </td>
                </tr>

                <tr>
                  <td>
                    <LogoWidget src={headerImg} setImage={setHeaderImage} height={28} padding="32px 56px" />
                  </td>
                </tr>

                {daysUntil !== null && (
                  <tr>
                    <td align="center" style={{ padding: '20px 56px' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td
                              align="center"
                              valign="middle"
                              width={120}
                              height={120}
                              style={{ background: 'var(--bg)', borderRadius: '60px' }}
                            >
                              <div style={{ color: 'var(--accent)', fontSize: '64px', fontFamily: 'var(--body-font)' }}>
                                {daysUntil}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}

                <tr>
                  <td>
                    <HeaderWidget
                      value={eventTitle}
                      setValue={setTitle}
                      color="var(--heading)"
                      fontFamily="var(--heading-font)"
                      fontSize="40px"
                      fontWeight={400}
                      lineHeight="48px"
                      textAlign="center"
                      padding="20px 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <ImageWidget
                      src={bodyImg}
                      setImage={setMainImage}
                      size="original"
                      width={400}
                      height={200}
                      borderRadius={imageRadius}
                      padding="20px 56px"
                    />
                  </td>
                </tr>

                {sessionDateLabel && (
                  <tr>
                    <td align="center" style={{ padding: '12px 56px' }}>
                      <div
                        style={{
                          color: 'var(--body)',
                          fontSize: '14px',
                          fontFamily: 'var(--body-font)',
                          textAlign: 'center',
                        }}
                      >
                        {sessionDateLabel}
                      </div>
                    </td>
                  </tr>
                )}
                {sessionTimeLabel && (
                  <tr>
                    <td align="center" style={{ padding: '12px 56px' }}>
                      <div
                        style={{
                          color: 'var(--body)',
                          fontSize: '14px',
                          fontFamily: 'var(--body-font)',
                          textAlign: 'center',
                        }}
                      >
                        {sessionTimeLabel}
                      </div>
                    </td>
                  </tr>
                )}
                {durationMinutes !== null && (
                  <tr>
                    <td align="center" style={{ padding: '12px 56px' }}>
                      <div
                        style={{
                          color: 'var(--body)',
                          fontSize: '14px',
                          fontFamily: 'var(--body-font)',
                          textAlign: 'center',
                        }}
                      >
                        {durationMinutes} Minutes Duration
                      </div>
                    </td>
                  </tr>
                )}

                <tr>
                  <td align="center" style={{ padding: '20px 56px' }}>
                    <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                      <tbody>
                        <tr>
                          <td
                            align="center"
                            valign="middle"
                            style={{ background: 'var(--button-color)', borderRadius: buttonRadius, height: '48px' }}
                          >
                            <a
                              href={anchorHref}
                              onClick={(e) => e.preventDefault()}
                              style={{
                                textDecoration: 'none',
                                display: 'inline-block',
                                padding: '0 40px',
                                height: '100%',
                                textAlign: 'center',
                              }}
                            >
                              <ContentEditable
                                value={_cta}
                                setValue={setCta}
                                style={{
                                  color: 'var(--button-text)',
                                  fontSize: '14px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '48px',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'nowrap',
                                }}
                              />
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </td>
                </tr>

                <tr>
                  <td>
                    <SocialWidget
                      links={[
                        { platform: ICONS.FACEBOOK, href: '#' },
                        { platform: ICONS.INSTAGRAM, href: '#' },
                        { platform: ICONS.TWITTER, href: '#' },
                      ]}
                      color="var(--heading)"
                      padding="20px 56px 0 56px"
                    />
                  </td>
                </tr>

                <tr>
                  <td>
                    <div style={{ opacity: 0.75 }}>
                      <ParagraphWidget
                        value={DEFAULTS.taglineText}
                        color="var(--body)"
                        fontSize="12px"
                        fontFamily="var(--body-font)"
                        textAlign="center"
                        width={300}
                        padding="20px 0 40px 0"
                      />
                    </div>
                    <FooterWidget
                      value={DEFAULTS.footerText}
                      src=""
                      brandliveLogo={brandliveLogo}
                      privacyPolicyLabel={privacyPolicyLabel}
                      bodyColor={bodyColor}
                      borderRadius={containerRadius}
                      accentColor={accent}
                      color={footerTextColor}
                      background={footerBackground}
                      {...FOOTER_CARD_STYLE}
                      fontFamily="var(--body-font)"
                    />
                  </td>
                </tr>
              </tbody>
            </table>

            <table
              width={600}
              cellPadding={0}
              cellSpacing={0}
              border={0}
              role="presentation"
              style={{ maxWidth: '600px' }}
            >
              <tbody>
                {!!calendarLinks && (
                  <tr>
                    <td align="center" style={{ padding: '24px 0' }}>
                      <table cellPadding={0} cellSpacing={0} border={0} role="presentation">
                        <tbody>
                          <tr>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.iCal}
                              >
                                iCal
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.outlook}
                              >
                                Outlook
                              </a>
                            </td>
                            <td
                              style={{
                                padding: '0 4px',
                                color: 'var(--body)',
                                fontSize: '11px',
                                fontFamily: "'TASA Orbiter', sans-serif",
                                fontWeight: 600,
                                lineHeight: '16px',
                                letterSpacing: '0.22px',
                              }}
                            >
                              •
                            </td>
                            <td>
                              <a
                                style={{
                                  color: 'var(--body)',
                                  fontSize: '12px',
                                  fontFamily: 'var(--body-font)',
                                  fontWeight: 600,
                                  lineHeight: '20px',
                                  letterSpacing: '0.24px',
                                }}
                                href={calendarLinks.google}
                              >
                                Google
                              </a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  );
};
